/**
 * @fileoverview The shared entrypoint bootstrap for every app that mounts the
 * CIP `<App>` shell.
 *
 * The webapp (`webapp/src/index.tsx`) and PAI (`pai/src/main.tsx`) mount the
 * same application and differ only in the overrides they pass. Each used to
 * write its own bootstrap, and they drifted: PAI ended up with no stale-chunk
 * guard and no error telemetry at all, on a live production host
 * ([#3696](https://github.com/CodeinPlace/cip/issues/3696)). Everything an
 * entrypoint does *around* the render lives here instead, so an entrypoint is
 * one call plus its own overrides, and anything added later lands on both.
 *
 * What stays per-app:
 *   - `index.html`, because PAI links its own manifest
 *     ([#3542](https://github.com/CodeinPlace/cip/issues/3542)).
 *   - The `import './firebaseApp'` on the first line of each entrypoint. It is
 *     an import-order constraint (Firebase must initialise before anything
 *     touches it), which a function call cannot express.
 *   - The `components` overrides themselves, including course-specific ones.
 */

import {getAuth} from 'firebase/auth';
import {createRoot} from 'react-dom/client';
import {App} from './App';
import type {AppOverrides} from 'contexts/AppOverridesContext';
import {installProductionLogging, type CipWebAppId} from './productionLogging';
import {installStaleChunkGuard, reportChunkLoadReload} from './staleChunkGuard';
import {reportPageLoad} from './pageLoadReport';
import {startConfiguredServiceWorkers} from './serviceWorkerController';
import {watchForSlowColdLoad} from './slowColdLoadReport';

/**
 * Installs the shared runtime guards and renders the CIP application shell
 * into `#root`.
 *
 * Ordering is deliberate: the stale-chunk guard and the logging interception
 * are both installed before the first render, so a failure during mount is
 * already covered by them.
 *
 * @param params.webApp - Which client this is. Stamped onto every forwarded
 *   log entry so one app's errors stay distinguishable from another's; see
 *   {@link CipWebAppId}.
 * @param params.components - Component overrides handed to {@link App}.
 *   Omitted → no overrides, i.e. the stock CIP UI throughout.
 * @returns Nothing.
 * @throws If the host document has no `#root` element to mount into.
 */
export function bootstrapCipApp({
  webApp,
  components = {},
}: {
  webApp: CipWebAppId;
  components?: AppOverrides;
}): void {
  // A tab open across a release still asks for the previous build's chunk
  // filenames, and a poor connection drops chunks too; recover by reloading.
  // See `staleChunkGuard.ts`.
  installStaleChunkGuard();

  // Production-only; see `productionLogging.ts`.
  installProductionLogging({webApp});
  // After the line above, so the report reaches the logWarning callable; and
  // once auth has restored the user, so the callable can attribute it.
  const whenAuthReady = getAuth().authStateReady();
  reportChunkLoadReload({whenReadyToLog: whenAuthReady});
  reportPageLoad({whenReadyToLog: whenAuthReady});

  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root element #root not found');
  }
  const root = createRoot(container);

  void startConfiguredServiceWorkers();

  // Armed before the first render so the timer covers the whole cold load.
  watchForSlowColdLoad();

  root.render(<App components={components} />);
}
